// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
// Resumo de conferência do S-26 exibido antes de gerar o PDF.
import { useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { AlertTriangle, CheckCircle2, Download } from 'lucide-react';
import { computeS26LedgerTotals, computeS26SymbolWarnings, formatBR } from '../s26Sync';

export default function S26CheckDialog({ open, onOpenChange, values, onConfirm }) {
  const totals = useMemo(() => computeS26LedgerTotals(values), [values]);
  const warnings = useMemo(() => computeS26SymbolWarnings(values), [values]);
  const warnList = Object.entries(warnings);
  const saldoFinal = (values['904_42_S26TotalAmount'] || '').trim();

  const linha = (label, e, s) => (
    <div className="grid grid-cols-3 gap-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right tabular-nums">{formatBR(e)}</span>
      <span className="text-right tabular-nums">{formatBR(s)}</span>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="s26-check-dialog">
        <DialogHeader>
          <DialogTitle>Conferência antes de baixar</DialogTitle>
          <DialogDescription>Revise os totais e avisos da folha S-26 antes de gerar o PDF.</DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5 border border-border rounded-lg p-3">
          <div className="grid grid-cols-3 gap-2 text-xs font-semibold text-muted-foreground">
            <span>Totais</span><span className="text-right">Entradas</span><span className="text-right">Saídas</span>
          </div>
          {linha('Donativos', totals.donE, totals.donS)}
          {linha('Conta bancária/Cofre', totals.contaE, totals.contaS)}
          {linha('Outra', totals.outraE, totals.outraS)}
          {saldoFinal && (
            <div className="flex justify-between text-sm font-semibold border-t border-border pt-1.5 mt-1">
              <span>Saldo disponível no final do mês</span>
              <span className="tabular-nums" data-testid="s26-check-saldo">{saldoFinal}</span>
            </div>
          )}
        </div>

        {warnList.length > 0 ? (
          <div className="border border-red-500/40 bg-red-500/5 rounded-lg px-3 py-2 space-y-1" data-testid="s26-check-warnings">
            {warnList.map(([n, msg]) => (
              <p key={n} className="text-xs text-red-600 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Linha {n}: {msg}.
              </p>
            ))}
          </div>
        ) : (
          <p className="text-xs text-emerald-600 flex items-center gap-1.5" data-testid="s26-check-ok">
            <CheckCircle2 className="w-3.5 h-3.5" /> Nenhum aviso pendente.
          </p>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="s26-check-cancel-btn">Voltar e corrigir</Button>
          <Button onClick={() => { onOpenChange(false); onConfirm(); }} data-testid="s26-check-download-btn">
            <Download className="w-4 h-4 mr-1" /> Baixar PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
